import type { FC } from 'react';
import { RadioButtonList } from '@interstate/components/RadioButtonList';
import { Typography } from '@interstate/components/Typography';
import type { SelectableResultsViewProps } from '../types';
import {
    ResultsContainer,
    SelectableResultCard,
    ResultHeader,
    ResultNameRow,
    CoAppBadge,
    ContactInfo,
    VehicleInfo,
    ResultContentGrid,
    EmptyStateContainer
} from '../CreditAppSearch.styles';
import { ServiceUnavailableAlert } from '../../common/ServiceUnavailableAlert';
import {
    getCoBuyerName,
    getCreatedDateTime,
    getResultId,
    getFormattedPhone,
    getVehicleDescription,
    valueOrDash
} from '../utils/resultCardMappers';

export const SelectableResultsView: FC<SelectableResultsViewProps> = ({ results, error, selectedResultId, onSelect }) => {
    if (error) {
        if (error.hasNoResults) {
            return (
                <EmptyStateContainer data-testid="search-no-results">
                    <Typography variant="strong-sm" color="sem.color.surface.button.primary.rest">
                        No Results
                    </Typography>
                </EmptyStateContainer>
            );
        }
        if (error.hasError) {
            return <ServiceUnavailableAlert />;
        }
    }

    if (results.length === 0) {
        return (
            <EmptyStateContainer data-testid="search-empty-state">
                <Typography variant="strong-sm" color="sem.color.surface.button.primary.rest">
                    Enter search details to begin
                </Typography>
            </EmptyStateContainer>
        );
    }

    return (
        <ResultsContainer data-testid="search-results-container">
            <Typography variant="h4" data-testid="search-results-heading">
                Results ({results.length})
            </Typography>
            {results.map((result) => {
                const resultId = getResultId(result);
                const coBuyerName = getCoBuyerName(result);
                return (
                    <SelectableResultCard key={resultId} $selected={resultId === selectedResultId} data-testid="search-result-card">
                        <ResultHeader>
                            <ResultNameRow>
                                <RadioButtonList
                                    name={`credit-app-search-result-${resultId}`}
                                    data-testid="result-radio"
                                    displayLabel={false}
                                    sx={{
                                        width: 'max-content',
                                        '& .MuiFormControlLabel-root': { margin: 0 },
                                        '& .MuiFormControlLabel-label': {
                                            position: 'absolute',
                                            width: '1px',
                                            height: '1px',
                                            padding: 0,
                                            overflow: 'hidden',
                                            clip: 'rect(0, 0, 0, 0)',
                                            whiteSpace: 'nowrap'
                                        }
                                    }}
                                    value={selectedResultId ?? ''}
                                    options={[{ value: resultId, label: valueOrDash(result.customerName) }]}
                                    onChange={() => onSelect(resultId)}
                                />
                                <Typography variant="body-md" data-testid="customer-name">
                                    {valueOrDash(result.customerName)}
                                </Typography>
                                {coBuyerName && <CoAppBadge data-testid="co-app-badge">Co-App</CoAppBadge>}
                            </ResultNameRow>
                            <Typography variant="body-xs" data-testid="created-date">
                                Created: {getCreatedDateTime(result)}
                            </Typography>
                        </ResultHeader>

                        {coBuyerName && (
                            <Typography variant="body-sm" data-testid="co-buyer-name">
                                Co-Buyer: {coBuyerName}
                            </Typography>
                        )}

                        <ResultContentGrid>
                            <ContactInfo>
                                <Typography variant="body-xs" data-testid="customer-phone">
                                    Phone: {getFormattedPhone(result)}
                                </Typography>
                                <Typography variant="body-xs" data-testid="customer-email">
                                    Email: {valueOrDash(result.email)}
                                </Typography>
                                <Typography variant="body-xs" data-testid="credit-id">
                                    Credit ID: {valueOrDash(result.creditAppId)}
                                </Typography>
                            </ContactInfo>
                            <VehicleInfo>
                                <Typography variant="body-xs" data-testid="vehicle-description">
                                    Vehicle: {getVehicleDescription(result)}
                                </Typography>
                                <Typography variant="body-xs" data-testid="vehicle-vin">
                                    VIN: {valueOrDash(result.vin)}
                                </Typography>
                                <Typography variant="body-xs" data-testid="vehicle-stock">
                                    Stock#: {valueOrDash(result.stockNumber)}
                                </Typography>
                            </VehicleInfo>
                        </ResultContentGrid>
                    </SelectableResultCard>
                );
            })}
        </ResultsContainer>
    );
};
